import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import type { NavDropdownLink } from './NavDropdown';
import { isNavLinkActive, normalizeNavPath } from './navPath';
import { PRODUCT_LINKS, isProductNavActive } from './ProductNavDropdown';
import { SOLUTION_LINKS, isSolutionsNavActive } from './SolutionsNavDropdown';
import { isUseCasesNavActive } from './UseCasesNavDropdown';
import { useUseCaseNavLinks } from '../../context/UseCaseNavContext';
import { SignUpButton, TalkToFoundersButton } from '../CTAButtons';
import rhapsodyIcon from '../../assets/rhapsody_icon.svg';

type MobileNavMenuProps = {
  pathname: string;
};

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function getFocusableElements(container: HTMLElement) {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => !el.hasAttribute('disabled') && el.getAttribute('aria-hidden') !== 'true',
  );
}

function MobileNavSection({
  title,
  links,
  pathname,
  isActive,
  onNavigate,
}: {
  title: string;
  links: readonly NavDropdownLink[];
  pathname: string;
  isActive: (pathname: string) => boolean;
  onNavigate: () => void;
}) {
  const sectionActive = isActive(pathname);
  const [open, setOpen] = useState(sectionActive);
  const panelId = useId();

  useEffect(() => {
    if (sectionActive) {
      setOpen(true);
    }
  }, [pathname, sectionActive]);

  return (
    <div className={`nav-mobile-section${open ? ' is-open' : ''}${sectionActive ? ' has-active' : ''}`}>
      <button
        type="button"
        className={`nav-mobile-section-trigger${sectionActive ? ' nav-mobile-section-trigger--active' : ''}`}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
      >
        {title}
        <svg className="nav-mobile-chevron" width="8" height="8" viewBox="0 0 8 8" aria-hidden="true">
          <path
            d="M1 2.5L4 5.5L7 2.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.25"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      <div id={panelId} className="nav-mobile-section-panel" hidden={!open}>
        {links.map((item) => {
          const active = isNavLinkActive(pathname, item.to);
          return (
            <Link
              key={item.to}
              to={item.to}
              className={`nav-mobile-link${active ? ' nav-mobile-link--active' : ''}`}
              aria-current={active ? 'page' : undefined}
              onClick={onNavigate}
            >
              <span className="nav-mobile-link-label">{item.label}</span>
              <span className="nav-mobile-link-desc">{item.description}</span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export function MobileNavMenu({ pathname }: MobileNavMenuProps) {
  const [open, setOpen] = useState(false);
  const { links: useCaseLinks } = useUseCaseNavLinks();
  const menuId = useId();
  const toggleRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLElement>(null);

  const close = useCallback(() => {
    setOpen(false);
    toggleRef.current?.focus();
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    const onChange = () => {
      if (!mq.matches) {
        setOpen(false);
        toggleRef.current?.focus();
      }
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    document.body.classList.toggle('nav-mobile-open', open);
    return () => {
      document.body.style.overflow = '';
      document.body.classList.remove('nav-mobile-open');
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        close();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;

      const panelFocusable = getFocusableElements(panelRef.current);
      const focusable = toggleRef.current
        ? [toggleRef.current, ...panelFocusable]
        : panelFocusable;
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, close]);

  useEffect(() => {
    if (!open || !panelRef.current) return;
    const focusable = getFocusableElements(panelRef.current);
    focusable[0]?.focus();
  }, [open]);

  return (
    <>
      <button
        ref={toggleRef}
        type="button"
        className={`nav-mobile-toggle${open ? ' is-open' : ''}`}
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={open ? 'Close menu' : 'Open menu'}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="nav-mobile-toggle-icon" aria-hidden="true">
          <span />
          <span />
          <span />
        </span>
      </button>

      {typeof document !== 'undefined' &&
        createPortal(
          <div
            id={menuId}
            className={`nav-mobile-menu${open ? ' is-open' : ''}`}
            aria-hidden={!open}
            onClick={close}
          >
            <nav
              ref={panelRef}
              className="nav-mobile-menu-inner"
              role="dialog"
              aria-modal="true"
              aria-label="Navigation menu"
              onClick={(e) => e.stopPropagation()}
            >
              <Link
                to="/rhapsody"
                className={`nav-mobile-rhapsody${pathname.startsWith('/rhapsody') ? ' nav-mobile-rhapsody--active' : ''}`}
                aria-label="Rhapsody Engine"
                aria-current={pathname.startsWith('/rhapsody') ? 'page' : undefined}
                onClick={close}
              >
                <img src={rhapsodyIcon} alt="Rhapsody Engine" width={4756} height={549} />
                <span className="nav-mobile-rhapsody-caption" aria-hidden="true">
                  Engine
                  <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                    <path
                      d="M4 3L8.5 7L4 11"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.25"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </Link>
              <MobileNavSection
                title="Product"
                links={PRODUCT_LINKS}
                pathname={pathname}
                isActive={isProductNavActive}
                onNavigate={close}
              />
              <MobileNavSection
                title="Solutions"
                links={SOLUTION_LINKS}
                pathname={pathname}
                isActive={isSolutionsNavActive}
                onNavigate={close}
              />
              {useCaseLinks.length > 0 && (
                <MobileNavSection
                  title="Use Cases"
                  links={useCaseLinks}
                  pathname={pathname}
                  isActive={isUseCasesNavActive}
                  onNavigate={close}
                />
              )}
              <Link
                to="/pricing"
                className={`nav-mobile-top-link${isNavLinkActive(pathname, '/pricing') ? ' nav-mobile-top-link--active' : ''}`}
                aria-current={isNavLinkActive(pathname, '/pricing') ? 'page' : undefined}
                onClick={close}
              >
                Pricing
              </Link>
              <Link
                to="/blog"
                className={`nav-mobile-top-link${normalizeNavPath(pathname).startsWith('/blog') ? ' nav-mobile-top-link--active' : ''}`}
                aria-current={normalizeNavPath(pathname).startsWith('/blog') ? 'page' : undefined}
                onClick={close}
              >
                Blog
              </Link>
              <div className="nav-mobile-cta">
                <SignUpButton variant="ghost" analyticsLocation="nav" />
                <TalkToFoundersButton analyticsLocation="nav" />
              </div>
            </nav>
          </div>,
          document.body,
        )}
    </>
  );
}
